import React, { useEffect } from 'react';
import classes from './Reranker.module.scss';
import { ResourceName } from '../components/ResourceName';
import { NodeBox } from '../components/NodeBox';
import { Stack } from 'new/elements/Stack';
import { Button } from 'new/elements/Button';
import { ResourceConfig } from 'js/components/pipelines/cards/components/ResourceConfig';
import DataSourceIcon from 'js/components/data_sources/DataSourceIcon';
import useEntities from 'new/state/entities/repository/useEntities';
import Menu from 'js/components/elements/menu/Menu';
import { ReactComponent as DeleteIcon } from 'new/components/icons/close-cross.svg';
import useNexsetsAdapter from 'new/state/nexsets/useNexsetsAdapter';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import useCredentialsAdapter from 'new/state/credentials/adapter/useCredentialsAdapter';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import { RESOURCE_TYPE_CREDENTIALS } from 'js/constants/resourceTypes';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_CREDENTIAL_REQUEST, GET_DATA_SET_REQUEST } from 'js/constants/actionTypes';
import useRerankerEndpoint from 'new/elements/flows/ModalConfigureReranker/steps/ConfigStep/useRerankerEndpoint';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import { ModalConfigureReranker } from 'new/elements/flows/ModalConfigureReranker';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';

export const Reranker = (props) => {
  const { data } = props;

  const { getDataCredential } = useCredentialsAdapter();
  const { refreshPipelineById } = usePipelinesAdapter();
  const ragFlowId = useRagFlowId();
  const { deleteNexset, requestNexset } = useNexsetsAdapter();
  const { vendors } = useVendors();
  const { isEdit } = useDraftContext();

  const loadingNexset = useIsLoading(GET_DATA_SET_REQUEST);
  const loadingCredential = useIsLoading(GET_CREDENTIAL_REQUEST);

  const { data: nexset } = useEntities(`dataSets[${data.id}]`);
  const { data: parentNexset } = useEntities(`dataSets[${data.parent_data_set_id}]`);
  const { data: credential } = useEntities(`credentials[${nexset.data_credentials_id}]`);

  const selectedConnection = credential?.vendor_id
    ? vendors[credential.vendor_id]?.name
    : credential?.connector?.type;

  const { filteredFields } = useRerankerEndpoint(selectedConnection);

  const sourceMeta = useResourceMeta(RESOURCE_TYPE_CREDENTIALS, credential?.id);

  useEffect(() => {
    if (nexset?.data_credentials_id && !credential && !loadingCredential) {
      getDataCredential(nexset?.data_credentials_id);
    }
  }, [nexset, credential, loadingCredential]);

  useEffect(() => {
    if (!nexset?.data_credentials_id && !loadingNexset) {
      requestNexset(nexset?.id);
    }
  }, [nexset, loadingNexset]);

  const onClickDelete = async () => {
    await deleteNexset(data.id);
    refreshPipelineById(ragFlowId);
  };

  return (
    <NodeBox hideInfo data={data} className={classes.container}>
      <ResourceName
        {...props}
        hideActions
        RightView={
          isEdit ? (
            <Menu
              horizontalDots
              items={[
                {
                  label: 'Delete',
                  icon: <DeleteIcon />,
                  disabled: !isEdit,
                  onClick: onClickDelete,
                  dialogMessage: `Please confirm you want to delete this Reranker`,
                  primaryButtonColor: 'error',
                  discardLabel: 'Cancel',
                  confirmLabel: 'Delete',
                },
              ]}
            />
          ) : null
        }
      >
        RERANKER
      </ResourceName>
      <div>
        <Stack direction="row" alignItems="center" justifyContent="flex-start">
          <DataSourceIcon icon={sourceMeta?.icon} noBorder />
          <NodeBox.Title>{sourceMeta?.name}</NodeBox.Title>
        </Stack>
        <ChipWithCopy toCopy={data?.id}>ID {data?.id}</ChipWithCopy>
        <Stack marginBlock="8px" gap="8px">
          <ResourceConfig label="Credential" value={`${credential?.id} - ${credential?.name}`} />
          {Object.keys(nexset?.custom_config?.props || {}).map((key) => {
            const props = nexset?.custom_config?.props;
            const field = filteredFields.find((field) => field.name === key);
            return <ResourceConfig label={field?.display_name || key} value={props[key]} />;
          })}
        </Stack>
      </div>
      {isEdit && (
        <NodeBox.Footer className={classes.footer}>
          <ModalConfigureReranker
            ButtonComponent={
              <Button variant="outlined" size="small">
                Settings
              </Button>
            }
            parentNexset={parentNexset}
            editingNexset={nexset}
          />
        </NodeBox.Footer>
      )}
    </NodeBox>
  );
};
